import React, { useEffect, useRef, useState } from 'react';
import styles from './LegAnalysis.module.scss';
import { useTranslation } from 'react-i18next';
import InsightAI from '../../../common/InsightAI/InsightAI';
import InteractiveTileGrid from '../InteractiveTileGrid/InteractiveTileGrid';
import { useGetKpiSettingsQuery } from '../../../../redux/services/kpiSettingsByTypeAPI';
import { useGetBusinessPerformanceSummaryQuery } from '../../../../redux/services/businessPerformaceAPI';
import { useGetAGCAIDetailsMutation } from '../../../../redux/services/agcAIAPI';
import { useProfileParams } from '../../../../utils/common';
import { normalizeMetricSourceData } from '../../../../utils/agcUtil';
import { useAppSelector } from '../../../../redux/hooks';
import isEmpty from 'lodash/isEmpty';
import QMixTile, { QMixDetail } from './KPI/QMix/QMixTile';
import RenewalRateTile, { RenewalRateDetail } from './KPI/RenewalRate/RenewalRateTile';
import FAAPointsTile, { FAAPointsDetail } from './KPI/FAAPoints/FAAPointsTile';
import RubyPVTile, { RubyPVDetail } from './KPI/RubyPV/RubyPVTile';
import TotalQsTile, { TotalQsDetail } from './KPI/TotalQs/TotalQsTile';
import FPLegsTile, { FPLegsDetail } from './KPI/FPLegs/FPLegsTile';
import QCsTile, { QCsDetail } from './KPI/QCs/QCsTile';

const LegAnalysis: React.FC<CategoriesProps> = () => {
  const { t } = useTranslation();
  const { affiliateCode, aboNumber } = useProfileParams();
  const isExpanded = useAppSelector((state: any) => state.sideNavExpansion.isExpanded);
  const [kpiCodes, setKpiCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [aiContent, setAiContent] = useState<string>('');
  const hasRequestedRef = useRef(false);

  const { data: structureKpisResponse } = useGetKpiSettingsQuery(
    { type: 'business-performance-structure', affiliateCode },
    { refetchOnMountOrArgChange: true }
  );

  const { data: performanceSummaryResponse, isLoading } = useGetBusinessPerformanceSummaryQuery(
    { affiliateCode, aboNumber, payload: { kpis: kpiCodes } },
    {
      refetchOnMountOrArgChange: true,
      skip: Number(affiliateCode) === 0 || aboNumber === 0 || isEmpty(kpiCodes),
    }
  );

  const [getAGCAIDetails, { data: aiDetailsResp }] = useGetAGCAIDetailsMutation();

  useEffect(() => {
    const response = structureKpisResponse?.body || [];
    if (!isEmpty(response)) {
      const codesFromConfig = response.map((item: { code: string }) => ({
        code: item?.code === 'activationRate:90|30|60' ? 'activationRate90' : item?.code,
      }));

      const requiredCodes = [
        'qMix',
        'renewalRate010',
        'faaPoints',
        'rubyPv',
        'totalSPQualifiedMonthsLos',
        'foundersPlatinumLegCount',
        'garQC',
      ];

      const existing = new Set(codesFromConfig.map((c: any) => c.code));
      const ensured = [
        ...codesFromConfig,
        ...requiredCodes.filter((rc) => !existing.has(rc)).map((code) => ({ code })),
      ];

      setKpiCodes(ensured);
    }
  }, [structureKpisResponse]);

  useEffect(() => {
    if (!isEmpty(kpiCodes)) {
      setLoading(isLoading);
    }
  }, [isLoading, kpiCodes]);

  // Fetch AI summary for structure category
  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && !isEmpty(kpiCodes);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: kpiCodes } as any });
  }, [affiliateCode, aboNumber, kpiCodes, getAGCAIDetails]);

  // Extract AI content from response
  useEffect(() => {
    if (aiDetailsResp?.body) {
      const aiRaw = aiDetailsResp.body;
      // Check if it's a string, object with summary, or object with KPI codes
      const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.summary || '';
      const content = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';
      setAiContent(content);
    }
  }, [aiDetailsResp]);

  const apiKpis = performanceSummaryResponse?.body?.kpis || [];

  const summaryByCode = (code: string) => apiKpis.find((k: any) => k.kpiCode === code);

  // Transform QMix API data to segments format
  const qmixKpi = summaryByCode('qMix');
  const qmixSummary = normalizeMetricSourceData(qmixKpi, (item: any) =>
    item.key === 'qv' ? 'QV' : item.key === 'q1' ? 'Q1' : item.key === 'q2' ? 'Q2' : 'Other'
  );

  const renewalRateSummary = summaryByCode('renewalRate010');

  const faaPointsSummary = summaryByCode('faaPoints');

  const rubyPVSummary = summaryByCode('rubyPv');
  const totalQsSummary = summaryByCode('totalSPQualifiedMonthsLos');
  const fpLegsSummary = summaryByCode('foundersPlatinumLegCount');
  const qcsSummary = summaryByCode('garQC');

  return (
    <div className={styles.legAnalysis_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t('structure')}</div>
      </div>
      <InsightAI
        title={'structure_summary_title'}
        className={styles.insight_ai}
        content={aiContent}
        showDropdown={true}
        isDropdownEnabled={true}
      />

      <div className={styles.tiles_grid_container}>
        <InteractiveTileGrid
          gridColumns={isExpanded ? 3 : 4}
          tiles={[
            {
              id: 'qMix',
              kpiTile: <QMixTile summary={qmixSummary} loading={loading} />,
              kpiDetailTile: <QMixDetail />,
            },
            {
              id: 'renewalRate',
              kpiTile: <RenewalRateTile summary={renewalRateSummary} loading={loading} />,
              kpiDetailTile: <RenewalRateDetail />,
            },
            {
              id: 'faaPoints',
              kpiTile: <FAAPointsTile summary={faaPointsSummary} loading={loading} />,
              kpiDetailTile: <FAAPointsDetail />,
            },
            {
              id: 'rubyPv',
              kpiTile: <RubyPVTile summary={rubyPVSummary} loading={loading} />,
              kpiDetailTile: <RubyPVDetail />,
            },
            {
              id: 'totalSPQualifiedMonthsLos',
              kpiTile: <TotalQsTile summary={totalQsSummary} loading={loading} />,
              kpiDetailTile: <TotalQsDetail />,
            },
            {
              id: 'foundersPlatinumLegCount',
              kpiTile: <FPLegsTile summary={fpLegsSummary} loading={loading} />,
              kpiDetailTile: <FPLegsDetail />,
            },
            {
              id: 'garQC',
              kpiTile: <QCsTile summary={qcsSummary} loading={loading} />,
              kpiDetailTile: <QCsDetail />,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default LegAnalysis;
